import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/** @type {number} */
let savedScrollY = 0;
/** @type {boolean} */
let bodyScrollPinned = false;

function hasOpenScrollLockDialog() {
  return Boolean(document.querySelector('dialog[scroll-lock][open], details[scroll-lock][open]'));
}

/**
 * Keep page scroll in sync with open scroll-locking dialogs.
 * Avoid touch-action:none — that freezes homepage carousel/page swipe on mobile.
 */
export function syncDocumentScrollLock() {
  if (hasOpenScrollLockDialog()) {
    lockDocumentScroll();
  } else {
    unlockDocumentScroll();
  }
}

function lockDocumentScroll() {
  if (!document.documentElement.hasAttribute('scroll-lock')) {
    savedScrollY = window.scrollY || window.pageYOffset || 0;
  }

  document.documentElement.setAttribute('scroll-lock', '');
  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow = 'hidden';

  // Mobile only: pin body while search/menu is open (no touch-action changes)
  if (window.matchMedia('(max-width: 749px)').matches && !bodyScrollPinned) {
    bodyScrollPinned = true;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }
}

function unlockDocumentScroll() {
  const wasPinned = bodyScrollPinned;
  const y = savedScrollY;

  document.documentElement.removeAttribute('scroll-lock');
  document.documentElement.style.overflow = '';
  document.documentElement.style.height = '';
  document.documentElement.style.width = '';
  document.documentElement.style.touchAction = '';

  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';
  document.body.style.height = '';
  document.body.style.overflow = '';
  document.body.style.touchAction = '';
  bodyScrollPinned = false;

  if (wasPinned) {
    window.scrollTo(0, y);
    requestAnimationFrame(() => window.scrollTo(0, y));
  }
}

/**
 * Force-clear a stuck lock when no dialog is actually open.
 * Useful after search close / keyboard dismiss on mobile homepage.
 */
export function forceUnlockDocumentScroll() {
  if (hasOpenScrollLockDialog()) return;
  unlockDocumentScroll();
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    forceUnlockDocumentScroll();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());
    syncDocumentScrollLock();

    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  closeDialog = async () => {
    const { dialog } = this.refs;

    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      active.blur();
    }

    if (!dialog.open) {
      forceUnlockDocumentScroll();
      return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    try {
      await Promise.race([
        onAnimationEnd(dialog, undefined, {
          subtree: false,
        }),
        new Promise((resolve) => setTimeout(resolve, 300)),
      ]);
    } catch (_) {
      // ignore
    }

    if (dialog.open) {
      dialog.close();
    }
    dialog.classList.remove('dialog-closing');

    forceUnlockDocumentScroll();
    setTimeout(() => forceUnlockDocumentScroll(), 50);
    setTimeout(() => forceUnlockDocumentScroll(), 200);

    this.dispatchEvent(new DialogCloseEvent());
  };

  closeDialogOnClickOutside = (/** @type {MouseEvent} */ event) => {
    this.#handleClick(event);
  };

  closeDialogOnEscapePress = (/** @type {KeyboardEvent} */ event) => {
    this.#handleKeyDown(event);
  };

  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        syncDocumentScrollLock();
      }
    }
  },
  { capture: true }
);

window.addEventListener('pageshow', () => {
  forceUnlockDocumentScroll();
  syncDocumentScrollLock();
});

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    forceUnlockDocumentScroll();
    syncDocumentScrollLock();
  }
});

window.addEventListener(
  'orientationchange',
  () => {
    setTimeout(() => {
      forceUnlockDocumentScroll();
      syncDocumentScrollLock();
    }, 100);
  },
  { passive: true }
);

// If lock was left behind, clear it on the next user gesture when no dialog is open
document.addEventListener(
  'touchstart',
  () => {
    if (!hasOpenScrollLockDialog()) {
      forceUnlockDocumentScroll();
    }
  },
  { passive: true, capture: true }
);
